import { supabase } from '../../lib/supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

/**
 * Get chat completion from any AI provider via Supabase Edge Function
 *
 * @param {string} provider - Provider identifier (e.g., 'OPEN_AI', 'ANTHROPIC')
 * @param {string} model - Model name
 * @param {array} messages - Messages array
 * @param {object} options - Additional parameters
 */
export async function getChatCompletion(provider, model, messages, options = {}) {
  const { data, error } = await supabase.functions.invoke('ai-chat-completion', {
    body: {
      provider,
      model,
      messages,
      stream: false,
      parameters: options,
    },
  });

  if (error) {
    // Attempt to read the actual error body from the edge function response
    let message = error.message || 'ai-chat-completion failed';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch {}
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Stream chat completion from any AI provider
 */
export async function getStreamingChatCompletion(
  provider,
  model,
  messages,
  onChunk,
  onComplete,
  onError,
  options = {}
) {
  const payload = {
    provider,
    model,
    messages,
    stream: true,
    parameters: options,
  };

  try {
    // Get current session token for auth; fall back to anon key
    const { data: { session } } = await supabase.auth.getSession();
    const authToken = session?.access_token || SUPABASE_ANON_KEY;

    const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-chat-completion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response?.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader?.read();
      if (done) break;

      buffer += decoder?.decode(value, { stream: true });
      const lines = buffer?.split('\n');
      buffer = lines?.pop() || '';

      for (const line of lines) {
        if (line?.startsWith('data: ')) {
          try {
            const data = JSON.parse(line?.slice(6));
            
            if (data?.type === 'chunk' && data?.chunk) {
              onChunk(data?.chunk);
            } else if (data?.type === 'done') {
              onComplete();
            } else if (data?.type === 'error') {
              console.error('Streaming error from Edge Function:', data?.error);
              onError(new Error(data.error));
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    onError(error);
  }
}